/* Webinars -> Registrations and Attendance: one campaign's funnel and its
 * attendee list — registration state, provider/join-link state, attendance
 * duration/percentage, segment, CTA — with the operational actions:
 * confirm a shared-link registration, import the provider's registrant
 * export, retry a failed registration, cancel, and a manual attendance
 * correction (own capability, reason mandatory, audited server-side).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, TestBadge } from "@/components/common";
import { AlertTriangle, RefreshCcw, Users } from "lucide-react";
import {
  CampaignPicker, CampaignHeadline, METHOD_LABELS, RegStateBadge, SEGMENT_ORDER, StatTile,
  TextPromptDialog, parseRegistrantRows, useWebinarCaps, fmtInTz,
} from "@/components/webinar/webinarShared";

const PAGE = 50;
const REG_FILTERS = ["pending", "registration_pending", "external_registration_pending",
  "registration_link_sent", "registered", "registration_failed", "cancelled"];

export const canRetryRegistration = (e) => e.registration_state === "registration_failed"
  || (e.registration_state === "external_registration_pending" && e.retry_state === "retryable");

const AttendanceDialog = ({ enrollment, open, onOpenChange, onDone }) => {
  const [attended, setAttended] = useState(true);
  const [minutes, setMinutes] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !enrollment) return;
    const a = enrollment.attendance || {};
    setAttended(a.state !== "no_show");
    setMinutes(a.duration_minutes ? String(a.duration_minutes) : "");
    setReason("");
  }, [open, enrollment]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/webinar/enrollments/${enrollment.enrollment_id}/attendance`, {
        attended, duration_minutes: attended ? Number(minutes) || 0 : 0, reason: reason.trim(),
      });
      toast.success("Attendance corrected");
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="webinar-attendance-dialog">
        <DialogHeader><DialogTitle>Correct attendance — {enrollment?.contact_name}</DialogTitle></DialogHeader>
        <p className="text-[11px] text-muted-foreground">
          Overwrites the recorded attendance evidence and re-derives the segment. Post-webinar
          actions that have not already happened (CTA message/task, voice follow-up, no-show
          re-invite) may then run. Audited with your reason.
        </p>
        <label className="flex items-center gap-2 text-xs">
          <Switch checked={attended} onCheckedChange={setAttended} data-testid="webinar-attendance-attended" />
          {attended ? "Attended" : "No-show"}
        </label>
        {attended && (
          <div>
            <Label className="text-xs">Minutes attended</Label>
            <Input type="number" min="0" value={minutes} data-testid="webinar-attendance-minutes"
                   onChange={(e) => setMinutes(e.target.value)} />
          </div>
        )}
        <div>
          <Label className="text-xs">Reason (required)</Label>
          <Textarea rows={3} value={reason} data-testid="webinar-attendance-reason"
                    placeholder="e.g. Joined from a colleague's device — confirmed on call"
                    onChange={(e) => setReason(e.target.value)} />
        </div>
        <DialogFooter>
          <Button disabled={busy || !reason.trim() || (attended && !(Number(minutes) > 0))}
                  data-testid="webinar-attendance-submit" onClick={submit}>Save correction</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const WebinarRegistrations = ({ campaigns, campaignId, onCampaignChange, refreshKey }) => {
  const { canConfig, canCorrectAttendance } = useWebinarCaps();
  const [detail, setDetail] = useState(null);
  const [enrollments, setEnrollments] = useState(null);
  const [regFilter, setRegFilter] = useState("");
  const [segFilter, setSegFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [prompt, setPrompt] = useState(null); // {kind: confirm|cancel|import, enrollmentId?}
  const [correcting, setCorrecting] = useState(null);
  const [retrying, setRetrying] = useState({});

  useEffect(() => { setOffset(0); }, [campaignId, regFilter, segFilter]);

  const load = useCallback(async () => {
    if (!campaignId) { setDetail(null); setEnrollments(null); return; }
    try {
      const qs = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
      if (regFilter) qs.set("registration_state", regFilter);
      if (segFilter) qs.set("segment", segFilter);
      const [c, e] = await Promise.all([
        api.get(`/webinar/campaigns/${campaignId}`),
        api.get(`/webinar/campaigns/${campaignId}/enrollments?${qs}`),
      ]);
      setDetail(c.data);
      setEnrollments(e.data);
    } catch (err) { toast.error(errDetail(err)); }
  }, [campaignId, offset, regFilter, segFilter]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const runPrompt = async (value) => {
    try {
      if (prompt.kind === "confirm") {
        await api.post(`/webinar/enrollments/${prompt.enrollmentId}/confirm-external-registration`,
                       { evidence: value });
        toast.success("Registration confirmed");
      } else if (prompt.kind === "cancel") {
        await api.post(`/webinar/enrollments/${prompt.enrollmentId}/cancel`, { reason: value });
        toast.success("Registration cancelled");
      } else if (prompt.kind === "import") {
        const rows = parseRegistrantRows(value);
        const r = await api.post(`/webinar/campaigns/${campaignId}/import-registrants`,
                                 { rows, evidence: `Pasted provider registrant export (${rows.length} rows)` });
        toast.success(`Confirmed ${r.data.confirmed.length}, already registered ${r.data.already_registered.length}, unmatched ${r.data.unmatched.length}`);
      }
      load();
    } catch (e) { toast.error(errDetail(e)); throw e; }
  };

  // Ref, not state: a same-tick double click must not send two requests
  // (the backend's compare-and-set claim would no-op the second anyway).
  const inFlight = useRef(new Set());
  const retry = async (enrollmentId) => {
    if (inFlight.current.has(enrollmentId)) return; // one in-flight retry per row
    inFlight.current.add(enrollmentId);
    setRetrying((r) => ({ ...r, [enrollmentId]: true }));
    try {
      const r = await api.post(`/webinar/enrollments/${enrollmentId}/retry-registration`,
                               { reason: "manual retry from Webinars → Registrations" });
      const st = r.data?.registration_state;
      if (st === "registration_failed") toast.error(`Retry failed again: ${r.data?.last_error || "provider error"}`);
      else toast.success(`Registration now ${(st || "").replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(errDetail(e)); } finally {
      inFlight.current.delete(enrollmentId);
      setRetrying((r) => ({ ...r, [enrollmentId]: false }));
    }
  };

  const f = detail?.funnel;
  const readiness = detail?.provider_readiness;
  const isLinkCampaign = readiness?.registration_method === "external_link";
  const campaignTz = detail?.campaign?.timezone;
  const sessionStart = Object.fromEntries((detail?.sessions || []).map((s) => [s.session_id, s.scheduled_start]));
  const total = enrollments?.total ?? 0;

  return (
    <div className="space-y-4" data-testid="webinar-registrations">
      <div className="flex items-center gap-2 flex-wrap">
        <CampaignPicker campaigns={campaigns} value={campaignId} onChange={onCampaignChange} />
        {detail && <CampaignHeadline campaign={detail.campaign} />}
      </div>

      {!campaignId ? (
        <EmptyState icon={Users} title="Choose a campaign" hint="Registrations and attendance are shown per campaign."
                    testId="webinar-registrations-empty" />
      ) : !detail ? null : (
        <>
          {readiness && !readiness.ready && (
            <div className="rounded-md border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 p-2 text-xs flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 text-[hsl(var(--danger))]" />
              <span><span className="font-medium">Provider blocked:</span> {readiness.blocked_reason}</span>
            </div>
          )}
          <Card data-testid="webinar-funnel-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading">Funnel</CardTitle>
              <p className="text-[11px] text-muted-foreground" data-testid="webinar-provider-method">
                Provider: {detail.campaign.provider} · {METHOD_LABELS[readiness?.registration_method] || readiness?.registration_method}
                {readiness?.uses_simulated_provider ? " · simulated in TEST MODE" : ""}
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                <StatTile label="Enrolled" value={f.enrolled_total} testId="webinar-stat-enrolled" />
                <StatTile label="Registered" value={f.registered_total} testId="webinar-stat-registered" />
                <StatTile label="Link sent (unconfirmed)" value={(f.link_sent_total ?? 0) + (f.external_pending_total ?? 0)} testId="webinar-stat-link-sent" />
                <StatTile label="Registration failed" value={f.registration_failed_total}
                          tone={f.registration_failed_total ? "danger" : undefined} testId="webinar-stat-failed" />
                <StatTile label="Attended" value={f.attended_total} testId="webinar-stat-attended" />
                <StatTile label="No-shows" value={f.no_show_total} testId="webinar-stat-noshow" />
                <StatTile label="CTA eligible" value={f.cta_eligible_total} testId="webinar-stat-cta" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {SEGMENT_ORDER.map((seg) => (
                  <button key={seg} type="button" onClick={() => setSegFilter(segFilter === seg ? "" : seg)}
                          className={`rounded-md border p-2 text-left ${segFilter === seg ? "ring-2 ring-primary" : ""}`}
                          data-testid={`webinar-segment-${seg}`}>
                    <p className="text-[11px] text-muted-foreground">{f.segment_labels[seg]}</p>
                    <p className="text-base font-semibold tabular-nums">{f.segments[seg] ?? 0}</p>
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
                <span>Cancelled: {f.cancelled_total}</span>
                <span>CTA messages sent: {f.cta_message_sent_total}</span>
                <span>CTA tasks: {f.cta_task_total}</span>
                <span>Voice follow-ups dispatched: {f.anika_followup_total}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
              <CardTitle className="text-sm font-heading">Attendees ({total})</CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={regFilter || "__all__"} onValueChange={(v) => setRegFilter(v === "__all__" ? "" : v)}>
                  <SelectTrigger className="h-8 w-[210px]" data-testid="webinar-reg-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All registration states</SelectItem>
                    {REG_FILTERS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={segFilter || "__all__"} onValueChange={(v) => setSegFilter(v === "__all__" ? "" : v)}>
                  <SelectTrigger className="h-8 w-[170px]" data-testid="webinar-seg-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All segments</SelectItem>
                    {SEGMENT_ORDER.map((s) => <SelectItem key={s} value={s}>{f.segment_labels[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
                {isLinkCampaign && canConfig && (
                  <Button size="sm" variant="outline" data-testid="webinar-import-registrants"
                          onClick={() => setPrompt({ kind: "import" })}>
                    Import registrant export
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!enrollments || enrollments.items.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center" data-testid="webinar-no-enrollments">
                  {regFilter || segFilter ? "No attendees match these filters." : "No enrollments yet."}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="webinar-enrollments-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead><TableHead>Session</TableHead>
                        <TableHead>Registration</TableHead><TableHead>Attendance</TableHead>
                        <TableHead>Segment</TableHead><TableHead>CTA / follow-up</TableHead>
                        {(canConfig || canCorrectAttendance) && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {enrollments.items.map((e) => {
                        const a = e.attendance || {};
                        return (
                          <TableRow key={e.enrollment_id} data-testid={`webinar-enrollment-${e.enrollment_id}`}>
                            <TableCell className="text-xs">
                              <Link to={`/customers/${e.contact_id}`} className="hover:underline font-medium">
                                {e.contact_name || e.contact_id}
                              </Link> {e.is_test_record && <TestBadge show />}
                              <span className="block text-[10px] text-muted-foreground">{e.source}</span>
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{fmtInTz(sessionStart[e.session_id], campaignTz, { year: false })}</TableCell>
                            <TableCell className="text-xs">
                              <RegStateBadge state={e.registration_state} testId={`webinar-reg-state-${e.enrollment_id}`} />
                              <span className="block text-[10px] text-muted-foreground mt-0.5">
                                {e.registration_method === "external_link" ? "shared link" : "provider API"}
                                {e.has_join_link ? " · join link ✓" : e.registration_state === "registered" ? " · no join link" : ""}
                                {e.external_link_delivery_status ? ` · message ${e.external_link_delivery_status.replace(/_/g, " ")}` : ""}
                                {e.registration_attempts > 1 ? ` · ${e.registration_attempts} attempts` : ""}
                              </span>
                              {e.provider_cancellation === "not_supported_manual_action_required" && (
                                <span className="block text-[10px] text-[hsl(var(--warning))]">Remove the provider-side seat by hand</span>
                              )}
                              {e.last_error && (
                                <p className="text-[10px] text-[hsl(var(--danger))] mt-0.5 max-w-[240px] truncate" title={e.last_error}>
                                  {e.last_error}
                                </p>
                              )}
                            </TableCell>
                            <TableCell className="text-xs" data-testid={`webinar-attendance-${e.enrollment_id}`}>
                              {a.state === "attended"
                                ? `${a.duration_minutes} min (${a.attended_pct}%)`
                                : (a.state || "unknown").replace(/_/g, " ")}
                              {a.source && (
                                <span className="block text-[10px] text-muted-foreground">
                                  {a.source === "manual_correction" ? "manually corrected" : a.source.replace(/_/g, " ")}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs">{e.segment_label || "—"}</TableCell>
                            <TableCell className="text-xs">
                              {e.cta?.eligible === true ? "eligible" : e.cta?.eligible === false ? "not eligible" : "—"}
                              <span className="block text-[10px] text-muted-foreground">
                                {e.cta?.message_sent_at ? "message queued" : ""}
                                {e.cta?.task_id ? `${e.cta?.message_sent_at ? " · " : ""}task` : ""}
                                {e.anika_followup?.state ? ` · voice ${e.anika_followup.state.replace(/_/g, " ")}` : ""}
                              </span>
                            </TableCell>
                            {(canConfig || canCorrectAttendance) && (
                              <TableCell className="text-right whitespace-nowrap">
                                {canConfig && e.registration_method === "external_link"
                                  && ["registration_link_sent", "external_registration_pending"].includes(e.registration_state) && (
                                  <Button size="sm" variant="outline" className="h-7 text-xs mr-1"
                                          data-testid={`webinar-confirm-${e.enrollment_id}`}
                                          onClick={() => setPrompt({ kind: "confirm", enrollmentId: e.enrollment_id })}>
                                    Confirm
                                  </Button>
                                )}
                                {canConfig && canRetryRegistration(e) && (
                                  <Button size="sm" variant="outline" className="h-7 text-xs mr-1"
                                          disabled={!!retrying[e.enrollment_id]}
                                          data-testid={`webinar-retry-${e.enrollment_id}`}
                                          onClick={() => retry(e.enrollment_id)}>
                                    <RefreshCcw className={`h-3 w-3 mr-1 ${retrying[e.enrollment_id] ? "animate-spin" : ""}`} /> Retry
                                  </Button>
                                )}
                                {canCorrectAttendance && e.registration_state !== "cancelled" && (
                                  <Button size="sm" variant="ghost" className="h-7 text-xs"
                                          data-testid={`webinar-correct-${e.enrollment_id}`}
                                          onClick={() => setCorrecting(e)}>
                                    Attendance
                                  </Button>
                                )}
                                {canConfig && e.registration_state !== "cancelled" && (
                                  <Button size="sm" variant="ghost" className="h-7 text-xs"
                                          data-testid={`webinar-cancel-${e.enrollment_id}`}
                                          onClick={() => setPrompt({ kind: "cancel", enrollmentId: e.enrollment_id })}>
                                    Cancel
                                  </Button>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
              {total > PAGE && (
                <div className="flex items-center justify-end gap-2 pt-2 text-xs">
                  <span className="text-muted-foreground">
                    {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
                  </span>
                  <Button size="sm" variant="outline" className="h-7" disabled={offset === 0}
                          onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
                  <Button size="sm" variant="outline" className="h-7" disabled={offset + PAGE >= total}
                          onClick={() => setOffset(offset + PAGE)}>Next</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <TextPromptDialog
        open={!!prompt}
        onOpenChange={(o) => { if (!o) setPrompt(null); }}
        testId="webinar-prompt"
        title={prompt?.kind === "confirm" ? "Confirm provider registration"
          : prompt?.kind === "cancel" ? "Cancel registration" : "Import provider registrant export"}
        label={prompt?.kind === "confirm" ? "Evidence (where you saw this person registered)"
          : prompt?.kind === "cancel" ? "Reason" : "Paste the export: one email (or phone) per line, optionally followed by ,join link"}
        multiline={prompt?.kind === "import"}
        placeholder={prompt?.kind === "import" ? "asha@example.com\nravi@example.com,https://event.webinarjam.com/go/…" : ""}
        onSubmit={runPrompt}
      />
      <AttendanceDialog enrollment={correcting} open={!!correcting}
                        onOpenChange={(o) => { if (!o) setCorrecting(null); }} onDone={load} />
    </div>
  );
};

export default WebinarRegistrations;
